import { inject, Injectable, OnDestroy } from '@angular/core';
import {
  ActiveConversationStore,
  ConversationStore,
  IdentityStore,
  MessageStore,
  ParticipantWithSourceId,
  TenantStore,
  UserStore,
} from '../stores';
import {
  Conversation,
  ConversationIncludeOption,
  ConversationLockRequest,
  Location,
  MessageKind,
  Participant,
  ParticipantKind,
  WsConversationArchived,
  WsConversationReadStatus,
  WsConversationUpdated,
  WsMessageUpdated,
  WsNewMessage,
  WsParticipantCreated,
  WsParticipantHadConversationOpen,
  WsParticipantReadStatus,
  WsParticipantWasTyping,
} from '@clientwire/api';
import { addEntities, setAllEntities, updateEntity } from '@ngrx/signals/entities';
import { MessagesWithConversationId } from '../interfaces';
import { FormControl, Validators } from '@angular/forms';
import { updateState } from '@angular-architects/ngrx-toolkit';
import { WireApiService } from './wire-api.service';
import { MessageStoreHelperService } from './message-store-helper.service';
import { NotificationTrackingService } from './notification-tracking.service';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { logger } from '../utils';
import { FilesToUpload } from '@akostm/orbit-ui';
import * as uuid from 'uuid';
import { Overlay } from '@angular/cdk/overlay';
import { Dialog } from '@angular/cdk/dialog';
import { LocationOverlayComponent } from '../atomic-design/templates';
import { WindowService } from './window.service';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root',
})
export class ConversationHelperService implements OnDestroy {
  readonly #activeConversationStore = inject(ActiveConversationStore);
  readonly #notificationTrackingService = inject(NotificationTrackingService);
  readonly #conversationStore = inject(ConversationStore);
  readonly #messageStore = inject(MessageStore);
  readonly #tenantStore = inject(TenantStore);
  readonly #userStore = inject(UserStore);
  readonly #identityStore = inject(IdentityStore);
  readonly #wireApiService = inject(WireApiService);
  readonly #messageStoreHelperService = inject(MessageStoreHelperService);
  readonly #translateService = inject(TranslateService);
  readonly #overlay = inject(Overlay);
  readonly #dialog = inject(Dialog);
  readonly #windowService = inject(WindowService);
  readonly #toastr = inject(ToastrService);
  readonly #unreadMessagesSubscription: Subscription | undefined;
  readonly #connectedConversations: {
    conversationId: string;
    unsubscribe: () => void | undefined;
  }[] = [];

  #conversationsUpdateUnsubscribe: (() => void) | null = null;
  #participantNotifiedUnsubscribe: (() => void) | null = null;

  //#region Lifecycle

  ngOnDestroy(): void {
    this.#connectedConversations.forEach((element) => {
      element.unsubscribe();
    });
    this.#unreadMessagesSubscription?.unsubscribe();
  }

  //#endregion

  //#region Conversations

  addToActiveConversations(conversations: Conversation[]) {
    // Add conversation to the active conversation store
    updateState(
      this.#activeConversationStore,
      'Add conversation to ActiveConversationStore',
      addEntities(
        conversations.map((c) => {
          const files: FilesToUpload[] = [];
          const messageSourceId = uuid.v4();
          const messageInput = new FormControl('', [Validators.maxLength(4096)]);
          return { ...c, files, messageSending: false as boolean, messageInput, messageSourceId };
        })
      )
    );

    // when the new conversation is the onlyone, make it in focus
    if (this.#activeConversationStore.entities().length === 1 && conversations[0]) {
      this.#activeConversationStore.setConversationIntoFocus(conversations[0].id);
    }

    // Add conversation to the message store
    const conversationIds = conversations.map((c) => c.id);
    const newConversations: MessagesWithConversationId[] = conversationIds.map((conversationId) => {
      return {
        id: conversationId,
        messages: [],
        messageIdsWithError: [],
        lastUpdateEvent: 'init',
        messageAppendLoading: false,
        messagePrependLoading: false,
        messagesLimit: this.#windowService.messagesLimit(),
      };
    });
    updateState(
      this.#messageStore,
      'Add conversations to MessageStore',
      addEntities(newConversations)
    );

    // Store the opened constersations state
    this.#activeConversationStore.storeActiveConversations();
    // Subscribe to websocket for the new conversations
    conversations.forEach((conversation) => {
      this.subscribeToConversation(conversation.id);
    });
  }

  async restoreActiveConversations() {
    // Restore the opened conversation ids from local storage
    const activeConversations = this.#activeConversationStore.getStoredConversationIds();
    if (
      !activeConversations ||
      (Array.isArray(activeConversations) && activeConversations.length === 0)
    )
      return;

    const conversationsByIds = await this.getConversationByIds(activeConversations);

    conversationsByIds.conversations?.forEach((conversation) => {
      if (conversation) {
        this.addToActiveConversations([conversation]);
      }
    });
  }

  async getConversationByIds(conversationIds: string[]) {
    return this.#wireApiService.conversationsApi.getConversationsByIds({
      conversationsByIdsRequest: { ids: conversationIds },
      includes: [
        ConversationIncludeOption.Participants,
        ConversationIncludeOption.ConversationType,
        ConversationIncludeOption.ListItemTemplateData,
        ConversationIncludeOption.ClientParticipantFrontendUrl,
        ConversationIncludeOption.ParticipantNotificationStatus,
      ],
    });
  }

  async addToConversationListByIds(conversationIds: string[]) {
    const conversationsByIds = await this.getConversationByIds(conversationIds);

    if (!conversationsByIds.conversations) return;

    // Filter conversations based on active filters using the store's method
    const visibleConversations: Conversation[] = [];

    for (const conversation of conversationsByIds.conversations) {
      if (this.#conversationStore.conversationPassesFilters(conversation)) {
        visibleConversations.push(conversation);
      } else {
        // Track as hidden (prevents double-counting if already tracked)
        this.#conversationStore.incrementHiddenConversationCount(conversation.id);
      }
    }

    // Add only visible conversations to the conversation store
    if (visibleConversations.length > 0) {
      updateState(
        this.#conversationStore,
        'Add conversations to ConversationStore',
        setAllEntities([...visibleConversations, ...this.#conversationStore.entities()])
      );
    }
  }

  async archiveAndCloseConversation(conversationId: string) {
    this.#conversationStore.archiveConversation(conversationId);
    this.#activeConversationStore.removeConversation(conversationId);
  }

  //#endregion

  //#region Messages

  async createNewMessage(
    conversationId: string,
    kind: MessageKind,
    messageSourceId?: string,
    text?: string,
    coordinates?: Location,
    formattedAddress?: string,
    imageSrc?: string
  ) {
    const tenantId = this.#tenantStore.id();
    if (!tenantId || !conversationId) {
      logger.error('Missing tenantId, conversationId or participantId');
      return;
    }
    const conversation = this.#activeConversationStore.entityMap()[conversationId];
    if (!conversation)
      throw new Error(
        this.#translateService.instant('SHARED.SERVICES.CONVERSATION.NOT_FOUND_ERROR')
      );

    const participant =
      this.#identityStore.identityType() === 'user'
        ? this.#findParticipantInConversationByUserId(conversation, this.#userStore.id())
        : this.#findParticipantInConversationByParticipantId(
            conversation,
            this.#identityStore.clientParticipantId()
          );

    if (!participant) {
      throw new Error(
        this.#translateService.instant('SHARED.SERVICES.CONVERSATION.PARTICIPANT_ID_MISSING')
      );
    }

    const localMessage = this.#messageStoreHelperService.createNewMessage(
      tenantId,
      conversationId,
      participant?.id ||
        this.#translateService.instant('SHARED.SERVICES.CONVERSATION.PARTICIPANT_ID_MISSING'),
      kind,
      messageSourceId,
      text,
      coordinates,
      formattedAddress,
      imageSrc
    );

    this.#messageStore.addMessages(conversationId, [localMessage], 'localMessage', 'prepend', true);
    this.#activeConversationStore.setNewMessageSourceId(conversationId);

    return await this.#messageStore.sendLocalMessageToServer(localMessage);
  }

  async loadMoreMessages(
    messageEntity: MessagesWithConversationId | undefined,
    position: 'prepend' | 'append' = 'append',
    limit = 10,
    fetchUntilHasMoreMessages = false
  ): Promise<void> {
    if (!messageEntity)
      throw new Error(this.#translateService.instant('SHARED.PAGES.DASHBOARD.NO_CONVERSATION'));

    const lastIndex = position === 'prepend' ? 0 : -1;
    const lastMessage = messageEntity.messages.slice(lastIndex)[0];
    if (
      !lastMessage ||
      (position === 'prepend' && messageEntity.messageAppendLoading === true) ||
      (position === 'append' && messageEntity.messagePrependLoading === true)
    ) {
      return;
    }

    const last_sequence = lastMessage.sequence;
    if (last_sequence) {
      await this.#messageStore.getMessagesOfConversation(
        messageEntity.id,
        limit,
        last_sequence,
        position,
        false,
        position === 'prepend' ? 'ASC' : 'DESC',
        fetchUntilHasMoreMessages
      );
    }
  }

  //#endregion

  //#region Participants

  #updateConversationParticipants(participant: Participant) {
    const conversation = this.#activeConversationStore
      .entities()
      .find((c) => c.id === participant.conversationId);
    if (!conversation) return;
    const oldParticipants = conversation?.participants;
    const newParticipants = oldParticipants ? [...oldParticipants, participant] : [participant];

    this.#activeConversationStore.updateConversation(conversation.id, {
      participants: newParticipants,
    });
    this.#conversationStore.updateConversation(conversation.id, {
      participants: newParticipants,
    });
  }

  #findParticipantInConversationByUserId(
    conversation: Conversation,
    userId: string
  ): Participant | undefined {
    if (conversation?.participants) {
      return conversation.participants.find((participant) => participant.userId === userId);
    }
    return undefined;
  }

  #findParticipantInConversationByParticipantId(
    conversation: Conversation,
    participantId: string | null
  ): Participant | undefined {
    if (conversation?.participants && participantId) {
      return conversation.participants.find((participant) => participant.id === participantId);
    }
    return undefined;
  }

  async addAndUpdateParticipantToConversation(
    conversationId: string,
    userId: string
  ): Promise<Participant> {
    const newParticipant = await this.#addUserAsParticipant(conversationId, userId);
    this.#updateConversationParticipants(newParticipant);
    return newParticipant;
  }

  async #addUserAsParticipant(conversationId: string, userId: string): Promise<Participant> {
    const firstName = this.#userStore.firstname ? this.#userStore.firstname() : null;
    const lastName = this.#userStore.lastname ? this.#userStore.lastname() : null;
    let displayName = null;
    if (firstName && lastName) {
      displayName = `${firstName} ${lastName}`;
    } else if (firstName) {
      displayName = firstName;
    } else if (lastName) {
      displayName = lastName;
    }

    return await this.#activeConversationStore.addParticipantToConversation(conversationId, {
      id: userId,
      kind: ParticipantKind.User,
      userId: userId,
      displayName,
    });
  }

  updateConversationReadStatus(payload: WsConversationReadStatus) {
    this.#activeConversationStore.updateConversation(payload.conversationId, {
      readStatus: {
        lastMessageSeen: payload.lastMessageSequenceSeen,
        unreadMessageCount: payload.unreadMessagesCount ?? 0,
      },
    });
    this.#conversationStore.updateConversation(payload.conversationId, {
      readStatus: {
        lastMessageSeen: payload.lastMessageSequenceSeen,
        unreadMessageCount: payload.unreadMessagesCount ?? 0,
      },
    });
  }

  updateConversationArchived(conversationId: string, archived: boolean) {
    this.#activeConversationStore.updateConversation(conversationId, {
      archived: archived,
    });
    this.#conversationStore.updateConversation(conversationId, {
      archived: archived,
    });

    if (archived) {
      if (this.#conversationStore.showArchiveOnly()) {
        const conversation = this.#activeConversationStore.entityMap()[conversationId];
        if (conversation) {
          this.#conversationStore.addConversation(conversation);
        }
      } else {
        this.#conversationStore.removeConversation(conversationId);
      }
    } else if (this.#conversationStore.showArchiveOnly()) {
      this.#conversationStore.removeConversation(conversationId);
    } else {
      const conversation = this.#activeConversationStore.entityMap()[conversationId];
      if (conversation) {
        this.#conversationStore.addConversation(conversation);
      }
    }
  }

  updateConversation(payload: WsConversationUpdated) {
    this.#activeConversationStore.updateConversation(payload.conversation.id, {
      participants: payload.conversation.participants,
      lockNote: payload.conversation.lockNote,
      lockExpiresAt: payload.conversation.lockExpiresAt,
      conversationData: payload.conversation.conversationData,
      labels: payload.conversation.labels,
    });

    this.#conversationStore.updateConversation(payload.conversation.id, {
      participants: payload.conversation.participants,
      lockNote: payload.conversation.lockNote,
      lockExpiresAt: payload.conversation.lockExpiresAt,
      conversationData: payload.conversation.conversationData,
      labels: payload.conversation.labels,
    });
  }

  //#endregion

  //#region Websocket Subscriptions
  subscribeToConversation(conversationId: string) {
    const tenantId = this.#tenantStore.id();
    if (!tenantId) return;
    logger.debug('Subscribing to conversation: ' + conversationId);

    // Check if the previous subscription already exists then unsubscribe first
    if (this.#connectedConversations.find((element) => element.conversationId === conversationId)) {
      this.unsubscribeFromConversation(conversationId);
    }

    const unsubscribe = this.#wireApiService.subscribeToConversation(
      conversationId,
      async (data) => {
        switch (data.type) {
          case 'NEW_MESSAGE': {
            logger.debug('Received new NEW_MESSAGE:', data);
            await this.#messageStore.addMessages(
              conversationId,
              [(data as WsNewMessage).message],
              'socketMessage',
              'prepend',
              true
            );
            break;
          }
          case 'MESSAGE_UPDATED': {
            logger.debug('Received new MESSAGE_UPDATED:', data);
            const message = (data as WsMessageUpdated).message;
            this.#messageStore.replaceMessage(conversationId, message, 'socketMessage');
            break;
          }
          case 'PARTICIPANT_READ_STATUS': {
            logger.debug('Received new PARTICIPANT_READ_STATUS:', data);
            const typedData = data as WsParticipantReadStatus;
            const { type: _type, participantId, ...participantData } = typedData;
            const participant = {
              id: participantId,
              ...participantData,
            };
            this.#updateParticipantFieldsInConversation(participant);
            break;
          }
          case 'PARTICIPANT_HAD_CONVERSATION_OPEN': {
            logger.debug('Received new PARTICIPANT_HAD_CONVERSATION_OPEN:', data);
            const typedData = data as WsParticipantHadConversationOpen;
            const formattedData: Partial<Participant> = {
              id: typedData.participantId,
              conversationId: typedData.conversationId,
              hadConversationOpenAt: typedData.hadConversationOpenAt,
            };
            this.#updateParticipantFieldsInConversation(formattedData);
            break;
          }
          case 'PARTICIPANT_WAS_TYPING': {
            logger.debug('Received new PARTICIPANT_WAS_TYPING:', data);
            const typedData = data as WsParticipantWasTyping;
            const formattedData: Partial<ParticipantWithSourceId> = {
              id: typedData.participantId,
              conversationId: typedData.conversationId,
              wasTypingAt: typedData.wasTypingAt,
              sourceId: typedData.sourceId,
            };
            this.#updateParticipantFieldsInConversation(formattedData);
            break;
          }
          case 'CONVERSATION_ARCHIVED': {
            logger.debug('Received new CONVERSATION_ARCHIVED:', data);
            const wsConversationArchived = data as WsConversationArchived;
            this.updateConversationArchived(
              wsConversationArchived.conversationId,
              wsConversationArchived.archived
            );
            break;
          }
          case 'PARTICIPANT_CREATED': {
            logger.debug('Received new PARTICIPANT_CREATED:', data);
            const wsParticipantCreated = data as WsParticipantCreated;

            this.#activeConversationStore.updateConversation(conversationId, {
              participants: wsParticipantCreated.participants,
            });
            break;
          }
          default: {
            logger.debug('unhandled event: ', data);
          }
        }
      }
    );

    logger.debug('Subscribed to conversation: ' + conversationId);
    this.#connectedConversations.push({ conversationId, unsubscribe });
  }

  #updateParticipantFieldsInConversation(newParticipantFields: Partial<ParticipantWithSourceId>) {
    if (!newParticipantFields.conversationId) return;
    const conversation =
      this.#activeConversationStore.entityMap()[newParticipantFields.conversationId];
    if (!conversation) return;
    const oldParticipants = conversation?.participants;
    if (!oldParticipants) {
      logger.error('No participants found in the conversation');
      return;
    }
    const oldParticipant = oldParticipants?.find((p) => p.id === newParticipantFields.id);
    if (!oldParticipant) {
      logger.error('The participant does not found in the conversation participants list');
      return;
    }
    const newParticipant = { ...oldParticipant, ...newParticipantFields };
    const newParticipants = oldParticipants.map((p) =>
      p.id === newParticipantFields.id ? newParticipant : p
    );

    this.#activeConversationStore.updateConversation(conversation.id, {
      participants: newParticipants,
    });
    this.#conversationStore.updateConversation(conversation.id, {
      participants: newParticipants,
    });
  }

  subscribeToUnreadMessages() {
    logger.debug('TODO: Subscribing to unread messages');
  }

  subscribeToConversationsUpdated() {
    this.#conversationsUpdateUnsubscribe = this.#wireApiService.subscribeToConversationUpdates(
      (payload) => {
        this.updateConversation(payload);
      }
    );
  }

  subscribeToParticipantNotifications() {
    this.#participantNotifiedUnsubscribe = this.#wireApiService.subscribeToParticipantNotified(
      (wsParticipantNotified) => {
        logger.info('Received PARTICIPANT_NOTIFIED:', wsParticipantNotified);

        const conversationId = wsParticipantNotified.conversationId;

        // Always update the participant in the conversation store (for conversation list)
        const conversationInStore = this.#conversationStore.entityMap()[conversationId];
        if (conversationInStore?.participants) {
          const updatedParticipants = conversationInStore.participants.map((p) =>
            p.id === wsParticipantNotified.participantId ? wsParticipantNotified.participant : p
          );
          this.#conversationStore.updateConversation(conversationId, {
            participants: updatedParticipants,
          });
        }

        // Also update the participant in the active conversation store if this conversation is active
        const activeConversation = this.#activeConversationStore.entityMap()[conversationId];
        if (activeConversation?.participants) {
          const updatedActiveParticipants = activeConversation.participants.map((p) =>
            p.id === wsParticipantNotified.participantId
              ? { ...wsParticipantNotified.participant, sourceId: p.sourceId }
              : p
          );
          this.#activeConversationStore.updateConversation(conversationId, {
            participants: updatedActiveParticipants,
          });

          // Show toast notification if:
          // 1. This is the conversation in focus
          // 2. It wasn't initiated from this frontend instance (check correlation UUID)
          const isInFocusConversation =
            this.#activeConversationStore.inFocusConversationId() === conversationId;
          const shouldShowToast =
            isInFocusConversation &&
            !this.#notificationTrackingService.shouldSuppressNotification(
              wsParticipantNotified.correlationUuid
            );

          if (shouldShowToast) {
            // Format participant info same as API response
            const participant = wsParticipantNotified.participant;
            const { displayName, email, phoneNumber } = participant;
            const contactInfo = [email, phoneNumber].filter(Boolean).join(', ');
            const participantInfo = displayName ? `${displayName} (${contactInfo})` : contactInfo;

            // Show toast matching the API response format
            if (wsParticipantNotified.success) {
              const title = this.#translateService.instant(
                'SHARED.PAGES.DASHBOARD.PARTICIPANT_NOTIFIED.SUCCESS.TITLE'
              );
              const message = this.#translateService.instant(
                'SHARED.PAGES.DASHBOARD.PARTICIPANT_NOTIFIED.SUCCESS.MESSAGE',
                { info: participantInfo }
              );
              this.#toastr.success(message, title);
            } else {
              const title = this.#translateService.instant(
                'SHARED.PAGES.DASHBOARD.PARTICIPANT_NOTIFIED.FAILED.TITLE'
              );
              const message = this.#translateService.instant(
                'SHARED.PAGES.DASHBOARD.PARTICIPANT_NOTIFIED.FAILED.MESSAGE',
                { info: participantInfo }
              );
              this.#toastr.error(message, title, {
                toastClass: 'ngx-toastr toastr-error-notification-failed',
              });
            }
          }
        }
      }
    );
  }

  unsubscribeFromConversation(conversationId: string) {
    const connection = this.#connectedConversations.find(
      (element) => element.conversationId === conversationId
    );
    connection?.unsubscribe();
    this.#removeConnectionFromArray(conversationId);
  }

  unsubscribeFromConversationsUpdated() {
    if (this.#conversationsUpdateUnsubscribe) {
      this.#conversationsUpdateUnsubscribe();
    }
  }

  unsubscribeFromParticipantNotifications() {
    if (this.#participantNotifiedUnsubscribe) {
      this.#participantNotifiedUnsubscribe();
    }
  }

  #removeConnectionFromArray(conversationId: string) {
    const index = this.#connectedConversations.findIndex(
      (element) => element.conversationId === conversationId
    );
    if (index > -1) {
      this.#connectedConversations.splice(index);
    }
  }

  //#endregion

  async createConversationLabels(conversationId: string, label: string) {
    try {
      const response = await this.#wireApiService.conversationLabelsApi.updateConversationLabels({
        conversationId: conversationId,
        conversationLabelPutRequest: {
          labels: [label],
        },
        includes: [
          ConversationIncludeOption.Participants,
          ConversationIncludeOption.ConversationType,
          ConversationIncludeOption.ListItemTemplateData,
          ConversationIncludeOption.ClientParticipantFrontendUrl,
          ConversationIncludeOption.ParticipantNotificationStatus,
        ],
      });

      updateState(
        this.#conversationStore,
        'Update conversation in ConversationStore',
        updateEntity({ id: conversationId, changes: { ...response } })
      );
      updateState(
        this.#activeConversationStore,
        'Update conversation in ActiveConversationStore',
        updateEntity({ id: conversationId, changes: { ...response } })
      );
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errorMessage = (err as any)?.error?.message;
      if (errorMessage) {
        updateState(this.#conversationStore, 'Set error in ConversationStore', {
          error: errorMessage,
        });
        updateState(this.#activeConversationStore, 'Set error in ActiveConversationStore', {
          error: errorMessage,
        });
      } else {
        logger.error(err);
      }
    }
  }

  async deleteConversationLabel(conversationId: string, label: string) {
    try {
      const response = await this.#wireApiService.conversationLabelsApi.deleteConversationLabel({
        conversationId: conversationId,
        label: label,
        includes: [
          ConversationIncludeOption.Participants,
          ConversationIncludeOption.ConversationType,
          ConversationIncludeOption.ListItemTemplateData,
          ConversationIncludeOption.ClientParticipantFrontendUrl,
          ConversationIncludeOption.ParticipantNotificationStatus,
        ],
      });
      updateState(
        this.#conversationStore,
        'Update conversation in ConversationStore',
        updateEntity({ id: conversationId, changes: { ...response } })
      );
      updateState(
        this.#activeConversationStore,
        'Update conversation in ActiveConversationStore',
        updateEntity({ id: conversationId, changes: { ...response } })
      );
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errorMessage = (err as any)?.error?.message;
      if (errorMessage) {
        updateState(this.#conversationStore, 'Set error in ConversationStore', {
          error: errorMessage,
        });
        updateState(this.#activeConversationStore, 'Set error in ActiveConversationStore', {
          error: errorMessage,
        });
      } else {
        logger.error(err);
      }
    }
  }

  async lockConversation(conversationId: string, lockConversationRequest: ConversationLockRequest) {
    try {
      const response = await this.#wireApiService.conversationsApi.lockConversation({
        conversationId: conversationId,
        conversationLockRequest: lockConversationRequest,
        includes: [
          ConversationIncludeOption.Participants,
          ConversationIncludeOption.ConversationType,
          ConversationIncludeOption.ListItemTemplateData,
          ConversationIncludeOption.ClientParticipantFrontendUrl,
          ConversationIncludeOption.ParticipantNotificationStatus,
        ],
      });
      updateState(
        this.#conversationStore,
        'Update conversation in ConversationStore',
        updateEntity({ id: conversationId, changes: { ...response } })
      );
      updateState(
        this.#activeConversationStore,
        'Update conversation in ActiveConversationStore',
        updateEntity({ id: conversationId, changes: { ...response } })
      );
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errorMessage = (err as any)?.error?.message;
      if (errorMessage) {
        updateState(this.#conversationStore, 'Set error in ConversationStore', {
          error: errorMessage,
        });
        updateState(this.#activeConversationStore, 'Set error in ActiveConversationStore', {
          error: errorMessage,
        });
      } else {
        logger.error(err);
      }
    }
  }

  async unlockConversation(conversationId: string) {
    try {
      const response = await this.#wireApiService.conversationsApi.unlockConversation({
        conversationId: conversationId,
        includes: [
          ConversationIncludeOption.Participants,
          ConversationIncludeOption.ConversationType,
          ConversationIncludeOption.ListItemTemplateData,
          ConversationIncludeOption.ClientParticipantFrontendUrl,
          ConversationIncludeOption.ParticipantNotificationStatus,
        ],
      });
      updateState(
        this.#conversationStore,
        'Update conversation in ConversationStore',
        updateEntity({ id: conversationId, changes: { ...response } })
      );
      updateState(
        this.#activeConversationStore,
        'Update conversation in ActiveConversationStore',
        updateEntity({ id: conversationId, changes: { ...response } })
      );
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errorMessage = (err as any)?.error?.message;
      if (errorMessage) {
        updateState(this.#conversationStore, 'Set error in ConversationStore', {
          error: errorMessage,
        });
        updateState(this.#activeConversationStore, 'Set error in ActiveConversationStore', {
          error: errorMessage,
        });
      } else {
        logger.error(err);
      }
    }
  }

  openLocationSelectorOverlay(conversationId: string, nextMessageSourceId?: string): void {
    const positionStrategy = this.#overlay.position().global().centerHorizontally().bottom();

    const dialogRef = this.#dialog.open(LocationOverlayComponent, {
      width: '61.25rem',
      panelClass: 'location-dialog',
      positionStrategy,
    });

    dialogRef.componentInstance?.sendLocation.subscribe(async (value) => {
      await this.createNewMessage(
        conversationId,
        'LOCATION',
        nextMessageSourceId ?? uuid.v4(),
        undefined,
        {
          longitude: value.longitude,
          latitude: value.latitude,
          accuracy: value.accuracy,
          source: value.source,
          language: value.language,
        },
        value.formatted_address
      );
      dialogRef.close();
    });
  }
}
